import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { AlertTriangle, X, Trash2, Loader2 } from 'lucide-react'

/**
 * DeleteConfirmationModal - A reusable delete confirmation dialog with safety features
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {function} onConfirm - Function to execute on confirmation
 * @param {string} title - Modal title
 * @param {string} message - Warning message
 * @param {string} itemName - Name of item(s) being deleted (for display)
 * @param {number} itemCount - Number of items being deleted
 * @param {array} cascadeEffects - Array of strings describing what will be deleted (cascade effects)
 * @param {boolean} requireTyping - Whether to require typing confirmation text
 * @param {string} confirmText - Text user must type to confirm (if requireTyping is true)
 * @param {boolean} isLoading - Whether deletion is in progress
 * @param {string} variant - 'danger' (default) or 'warning'
 */
const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Deletion',
  message = 'Are you sure you want to delete this item?',
  itemName = 'item',
  itemCount = 1,
  cascadeEffects = [],
  requireTyping = false,
  confirmText = 'DELETE',
  isLoading = false,
  variant = 'danger',
}) => {
  const [typedText, setTypedText] = useState('')
  const [hasConfirmed, setHasConfirmed] = useState(false)

  const handleClose = () => {
    if (!isLoading) {
      setTypedText('')
      setHasConfirmed(false)
      onClose()
    }
  }

  const handleConfirm = () => {
    if (requireTyping && typedText !== confirmText) {
      return
    }
    if (onConfirm) {
      onConfirm()
    }
  }

  const canConfirm = requireTyping ? typedText === confirmText : hasConfirmed

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className={`px-6 py-4 border-b border-gray-200 dark:border-gray-700 ${
            variant === 'danger' ? 'bg-red-50 dark:bg-red-900/20' : 'bg-orange-50 dark:bg-orange-900/20'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  variant === 'danger' 
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                    : 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
                }`}>
                  <AlertTriangle size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    {title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    This action cannot be undone
                  </p>
                </div>
              </div>
              {!isLoading && (
                <button
                  onClick={handleClose}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Close"
                >
                  <X size={20} className="text-gray-500 dark:text-gray-400" />
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            {/* Warning Message */}
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {message}
              </p>
              
              {/* Item Count */}
              {itemCount > 1 && (
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {itemCount} {itemName}{itemCount > 1 ? 's' : ''} will be deleted
                  </p>
                </div>
              )}

              {/* Cascade Effects Warning */}
              {cascadeEffects.length > 0 && (
                <div className={`mb-4 p-4 rounded-lg border-2 ${
                  variant === 'danger'
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
                }`}>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    ⚠️ This will also delete:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300">
                    {cascadeEffects.map((effect, index) => (
                      <li key={index}>{effect}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Type to Confirm */}
              {requireTyping && (
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Type <span className="font-mono text-red-600 dark:text-red-400">{confirmText}</span> to confirm:
                  </label>
                  <input
                    type="text"
                    value={typedText}
                    onChange={(e) => setTypedText(e.target.value)}
                    placeholder={confirmText}
                    disabled={isLoading}
                    className="w-full px-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-red-500 dark:focus:border-red-400 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                    autoFocus
                  />
                  {typedText && typedText !== confirmText && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                      Text does not match
                    </p>
                  )}
                </div>
              )}

              {/* Checkbox Confirmation (if not requiring typing) */}
              {!requireTyping && (
                <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <input
                    type="checkbox"
                    id="confirm-delete"
                    checked={hasConfirmed}
                    onChange={(e) => setHasConfirmed(e.target.checked)}
                    disabled={isLoading}
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <label
                    htmlFor="confirm-delete"
                    className="flex-1 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                  >
                    I understand this action cannot be undone and I want to proceed with the deletion.
                  </label>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={handleClose}
                disabled={isLoading}
                className="px-4 py-2 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!canConfirm || isLoading}
                className={`px-6 py-2 rounded-lg font-semibold text-white transition-colors flex items-center gap-2 ${
                  variant === 'danger'
                    ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-400'
                    : 'bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default DeleteConfirmationModal
